import {
    BODY_FONT_SIZE,
} from '../../../constants/generalConstants';

export const DEFAULT_AUDIO_NOTE_TITLE_FONT_SIZE = 1.125;
export const COMPACT_AUDIO_NOTE_TITLE_FONT_SIZE = 0.875;
export const ANNOTATION_AUDIO_NOTE_WIDTH_PX = 12.5 * BODY_FONT_SIZE;
export const READ_ONLY_ANNOTATION_AUDIO_NOTE_WIDTH_PX = 11 * BODY_FONT_SIZE;
export const MIN_AUDIO_NOTE_COMPACT_TITLE_WIDTH_PX = 13 * BODY_FONT_SIZE;
export const MIN_AUDIO_NOTE_INLINE_PLAYBACK_META_WIDTH_PX = 7 * BODY_FONT_SIZE;
export const MIN_AUDIO_NOTE_RECORDING_INLINE_PLAYBACK_META_WIDTH_PX = 1 * BODY_FONT_SIZE;

export interface IResolveAudioNoteLayoutArgs {
    containerWidth: number | null | undefined,
    inlineMetaWidth: number | null | undefined,
    isRecording?: boolean,
}

export interface IResolvedAudioNoteLayout {
    shouldShowInlinePlaybackMeta: boolean,
    titleFontSize: number,
}

/**
 * Annotation audio notes use fixed card widths, so keep that decision behind
 * one helper instead of repeating read-only checks across the renderer.
 */
export function resolveAnnotationAudioNoteWidthPx(readOnly: boolean): number {
    if (readOnly) {
        return READ_ONLY_ANNOTATION_AUDIO_NOTE_WIDTH_PX;
    }

    return ANNOTATION_AUDIO_NOTE_WIDTH_PX;
}

/**
 * Width measurements can briefly be missing during mount or while a playback
 * lane is hidden. Normalize those cases so layout decisions stay explicit.
 */
const normalizeAudioNoteWidth = (
    width: number | null | undefined,
): number | null => {
    if (
        typeof width !== 'number'
        || !Number.isFinite(width)
        || width <= 0
    ) {
        return null;
    }

    return width;
};

/**
 * Narrow audio-note cards do not have enough horizontal space for the title,
 * waveform, duration, and action buttons at the same time. Compact the title
 * and hide the inline playback meta until the layout has a real measured lane.
 */
export function resolveAudioNoteLayout({
    containerWidth,
    inlineMetaWidth,
    isRecording = false,
}: IResolveAudioNoteLayoutArgs): IResolvedAudioNoteLayout {
    const normalizedContainerWidth = normalizeAudioNoteWidth(containerWidth);
    const normalizedInlineMetaWidth = normalizeAudioNoteWidth(inlineMetaWidth);
    const minInlinePlaybackMetaWidth = isRecording
        ? MIN_AUDIO_NOTE_RECORDING_INLINE_PLAYBACK_META_WIDTH_PX
        : MIN_AUDIO_NOTE_INLINE_PLAYBACK_META_WIDTH_PX;

    return {
        shouldShowInlinePlaybackMeta: normalizedInlineMetaWidth != null
            && normalizedInlineMetaWidth >= minInlinePlaybackMetaWidth,
        titleFontSize: normalizedContainerWidth != null
            && normalizedContainerWidth < MIN_AUDIO_NOTE_COMPACT_TITLE_WIDTH_PX
            ? COMPACT_AUDIO_NOTE_TITLE_FONT_SIZE
            : DEFAULT_AUDIO_NOTE_TITLE_FONT_SIZE,
    };
}
